import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/features/auth/useAuth";
import { KPI_DEFS } from "@/features/kpi/definitions";
import {
  collectKpiValues,
  kpiBackend,
  listKpiEntries,
  periodStartFor,
  readKpiCollectMeta,
  readSharedCollectMeta,
  shiftPeriod,
  upsertKpiEntry,
  type KpiCollectResult,
  type KpiEntry,
  type KpiPeriodType,
} from "@/features/kpi/repo";
import { canReuseCollect, humanizeCollectorLine } from "@/features/kpi/collectCache";

export function useKpiWorkspace() {
  const { user } = useAuth();
  const [periodType, setPeriodType] = useState<KpiPeriodType>("month");
  const [periodStart, setPeriodStart] = useState(() => periodStartFor(new Date(), "month"));
  const [entries, setEntries] = useState<KpiEntry[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [backend, setBackend] = useState<"supabase" | "local">("supabase");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [collectNote, setCollectNote] = useState<string | null>(null);
  const [collectMeta, setCollectMeta] = useState(() => readKpiCollectMeta());

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, shared] = await Promise.all([
        listKpiEntries(periodType),
        readSharedCollectMeta(periodType),
      ]);
      setEntries(rows);
      setCollectMeta(shared ?? readKpiCollectMeta());
      setBackend(kpiBackend());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load KPI entries.");
    } finally {
      setLoading(false);
    }
  }, [periodType]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const entriesFor = useCallback(
    (start: string) => {
      const map = new Map<string, KpiEntry>();
      for (const entry of entries) {
        if (entry.periodStart === start) map.set(entry.kpiId, entry);
      }
      return map;
    },
    [entries],
  );

  const current = useMemo(() => entriesFor(periodStart), [entriesFor, periodStart]);
  const previousStart = useMemo(
    () => shiftPeriod(periodStart, periodType, -1),
    [periodStart, periodType],
  );
  const previous = useMemo(() => entriesFor(previousStart), [entriesFor, previousStart]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const def of KPI_DEFS) {
      const entry = current.get(def.id);
      next[def.id] = entry ? String(entry.value) : "";
    }
    setDraft(next);
    setSaved(false);
  }, [current]);

  const dirtyIds = useMemo(
    () =>
      KPI_DEFS.filter((def) => {
        const raw = (draft[def.id] ?? "").trim();
        const entry = current.get(def.id);
        if (raw === "" || !Number.isFinite(Number(raw))) return false;
        return !entry || entry.value !== Number(raw);
      }).map((def) => def.id),
    [current, draft],
  );

  const save = useCallback(
    async (ids = dirtyIds) => {
      setSaving(true);
      setError(null);
      try {
        for (const kpiId of ids) {
          const raw = (draft[kpiId] ?? "").trim();
          if (raw === "" || !Number.isFinite(Number(raw))) continue;
          await upsertKpiEntry(
            {
              kpiId,
              periodType,
              periodStart,
              value: Number(raw),
              note: current.get(kpiId)?.note ?? "",
            },
            user?.id,
          );
        }
        await refresh();
        setBackend(kpiBackend());
        setSaved(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not save KPI values.");
      } finally {
        setSaving(false);
      }
    },
    [current, dirtyIds, draft, periodStart, periodType, refresh, user?.id],
  );

  const collectAuto = useCallback(async (force = false) => {
    setCollecting(true);
    setError(null);
    setCollectNote(null);
    try {
      const result: KpiCollectResult = await collectKpiValues("recompute", periodType, { force });
      await refresh();
      setCollectMeta((await readSharedCollectMeta(periodType)) ?? readKpiCollectMeta());
      if (result.fromCache) {
        setCollectNote(result.collectors[0]?.note ?? "Showing last saved automatic values.");
        return;
      }
      const filled = (result.entries ?? []).length;
      const skipped = result.collectors
        .filter((item) => {
          if (item.status === "skipped" || item.status === "failed") return true;
          return /429|unavailable|webkey|migrated|failed|HOS /i.test(`${item.note ?? ""} ${item.error ?? ""}`);
        })
        .map((item) => humanizeCollectorLine(item.collector, item.note, item.error))
        .filter((line): line is string => Boolean(line))
        .join(" ");
      setCollectNote(
        filled
          ? `Filled ${filled} automatic value${filled === 1 ? "" : "s"} from live systems.${skipped ? ` ${skipped}` : ""}`
          : skipped || "Collectors ran but had no computable rows for this period.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load automatic KPI values.");
    } finally {
      setCollecting(false);
    }
  }, [periodType, refresh]);

  function switchPeriodType(type: KpiPeriodType) {
    if (type === periodType) return;
    setPeriodType(type);
    setPeriodStart(periodStartFor(new Date(), type));
  }

  return {
    periodType,
    periodStart,
    setPeriodStart,
    switchPeriodType,
    entries,
    current,
    previous,
    draft,
    setDraft,
    dirtyIds,
    backend,
    loading,
    saving,
    error,
    saved,
    save,
    collecting,
    collectNote,
    collectAuto,
    lastCollectAt: collectMeta?.at ?? null,
    collectFresh: canReuseCollect(collectMeta, Date.now(), periodType) &&
      collectMeta?.status !== "failed",
    reload: refresh,
  };
}
